import * as React from 'react';
import { useMemo, useState } from 'react';
import { Plus, Sparkles, X } from 'lucide-react';

import { ConfigEditorModal, ConfigSection, ConfigSummaryTile } from './config-editor-modal';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Button } from '@/components/ui/button';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { ApiError } from '@/lib/api/client';
import {
  useAddHealthDomainMutation,
  useHealthDomainsQuery,
  usePatchHealthDomainMutation,
  type HealthDomain,
} from '@/lib/api/health-domains';
import { useAdminProposalsQuery } from '@/lib/api/admin-proposals';
import { formatDate, titleCase } from '@/lib/utils/format';

/**
 * T-0114 / E07-F01 — Health-domain configuration editor.
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals-round4b.js → openDomainEditor`
 *   - README §4 modal table row `data-open-domains`
 *
 * Behavior:
 *   - Loads the live domain set for the engagement (active only by
 *     default; the modal could toggle to archived in a future pass).
 *   - The Lead may rename, reorder, soft-remove (`is_active: false`), or
 *     edit the description of a domain. Each save records a structured
 *     override blob.
 *   - Soft-remove is the destructive affordance — the prototype's "x"
 *     button. Hard-delete is blocked at the API layer per T-0070 spec.
 *   - "Add custom domain" is a UI-only stub in v1 — the backend exposes
 *     POST .../confirm for confirm-set; there's no single-row POST. The
 *     button is rendered with a tooltip noting the gap; a future ticket
 *     widens the API.
 *   - AI proposal accept/reject lives in the AI surface card. The
 *     `useConfirmHealthDomainsMutation` would be wired in by a future
 *     ticket once the AI proposal list endpoint surfaces in the client.
 *
 * Degrade-state handling (T-0114 acceptance criterion):
 *   - The "no active assessment" gate is enforced at the backend on
 *     PATCH; the modal still renders, but `saveError` surfaces the
 *     server message ("Domain edits not allowed while an assessment is
 *     in progress") so the consultant knows what to do.
 */

export interface DomainEditorModalProps {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DomainDraft {
  name: string;
  display_order: number;
  description: string | null;
  is_active: boolean;
  rationale: string;
}

/** Mirrors the API's override rationale floor: one short sentence. */
const MIN_RATIONALE_CHARS = 10;

function emptyDraft(d: HealthDomain): DomainDraft {
  return {
    name: d.name,
    display_order: d.display_order,
    description: d.description,
    is_active: d.is_active,
    rationale: '',
  };
}

export function DomainEditorModal({ engagementId, open, onOpenChange }: DomainEditorModalProps) {
  const query = useHealthDomainsQuery(open ? engagementId : undefined, { is_active: true });
  const domains = useMemo(() => query.data?.domains ?? [], [query.data]);
  // T-0122 admin proposal list — firm-admin-scoped read of AI-proposed
  // domain sets / domain ratings.
  const adminProposals = useAdminProposalsQuery(
    { type: 'domain', status: 'pending' },
    Boolean(open),
  );

  const [drafts, setDrafts] = useState<Record<string, DomainDraft>>({});
  const [showAddRow, setShowAddRow] = useState(false);
  const [addDraft, setAddDraft] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  });

  const isDirty = Object.keys(drafts).length > 0 || showAddRow;
  const mutation = usePatchHealthDomainMutation(engagementId);
  const addMutation = useAddHealthDomainMutation(engagementId);
  const [saveError, setSaveError] = useState<unknown>(null);

  const updateDraft = (domainId: string, updates: Partial<DomainDraft>) => {
    setDrafts((prev) => {
      const live = domains.find((d) => d.id === domainId);
      if (!live) return prev;
      const base: DomainDraft = prev[domainId] ?? emptyDraft(live);
      const next: DomainDraft = { ...base, ...updates };
      // Drop the entry if it matches the live baseline + has no rationale.
      const matches =
        next.name === live.name &&
        next.display_order === live.display_order &&
        next.description === live.description &&
        next.is_active === live.is_active;
      if (matches && next.rationale.trim().length === 0) {
        const copy = { ...prev };
        delete copy[domainId];
        return copy;
      }
      return { ...prev, [domainId]: next };
    });
  };

  const handleSave = async ({ closeOnSuccess }: { closeOnSuccess: boolean }) => {
    setSaveError(null);
    try {
      // T-0126 — add new custom domain first so the new row is in scope
      // for any reorder PATCHes that may follow.
      if (showAddRow) {
        const name = addDraft.name.trim();
        if (!name) {
          throw new ApiError(400, 'validation', 'New domain requires a name.', '');
        }
        await addMutation.mutateAsync({
          body: {
            name,
            description:
              addDraft.description.trim().length > 0 ? addDraft.description.trim() : null,
          },
        });
        setShowAddRow(false);
        setAddDraft({ name: '', description: '' });
      }
      for (const [domainId, draft] of Object.entries(drafts)) {
        const live = domains.find((d) => d.id === domainId);
        if (!live) continue;
        const rationale = draft.rationale.trim();
        if (rationale.length < MIN_RATIONALE_CHARS) {
          throw new ApiError(
            400,
            'validation',
            `Domain edits require a one-line rationale per row (at least ${MIN_RATIONALE_CHARS} characters). The audit trail captures it alongside the change.`,
            '',
          );
        }
        // The override taxonomy has no domain-specific tag; a domain edit is
        // "other", with the rationale carrying the reason.
        const body: Parameters<typeof mutation.mutateAsync>[0]['body'] = {
          override: { tag: 'other', rationale },
        };
        if (draft.name !== live.name) body.name = draft.name;
        if (draft.display_order !== live.display_order) body.display_order = draft.display_order;
        if (draft.description !== live.description) body.description = draft.description;
        if (draft.is_active !== live.is_active) body.is_active = draft.is_active;
        await mutation.mutateAsync({ domainId, body });
      }
      setDrafts({});
      if (closeOnSuccess) onOpenChange(false);
    } catch (err) {
      setSaveError(err);
    }
  };

  const counts = useMemo(() => {
    const active = domains.filter((d) => d.is_active).length;
    const firmDefault = 10; // PMI 8-step template
    return {
      active,
      firmDefault,
      drift: Math.max(0, active - firmDefault),
    };
  }, [domains]);

  const drift = counts.drift;

  return (
    <ConfigEditorModal
      open={open}
      onOpenChange={onOpenChange}
      bread="Health Check"
      title="Health-check domain configuration"
      sub="Configure the per-engagement health domain set. Firm default is 10 (PMI 8-step template). Domains are soft-removable; hard-delete is blocked once an assessment exists."
      isDirty={isDirty}
      onSave={handleSave}
      saving={mutation.isPending || addMutation.isPending}
      saveError={saveError ? <ErrorMessage error={saveError} /> : undefined}
      foot="Adding mid-engagement is additive (no migration needed). Removing requires migration of existing assessments — captured via override primitive."
      warning={
        drift > 0 ? (
          <span>
            <strong>Drift from firm default</strong> — this engagement diverges from the PMI 8-step
            template by {drift} domain{drift === 1 ? '' : 's'}. At closeout you can offer the change
            to the firm library if it is worth reusing.
          </span>
        ) : undefined
      }
      summary={
        query.isLoading ? (
          <div className="col-span-full flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading domains…
          </div>
        ) : (
          <>
            <ConfigSummaryTile
              label="Active domains"
              value={counts.active}
              sub="on this engagement"
            />
            <ConfigSummaryTile
              label="Firm default"
              value={counts.firmDefault}
              sub="PMI 8-step template"
            />
            <ConfigSummaryTile
              label="Override drift"
              value={drift}
              sub="custom rows vs. template"
              tone={drift === 0 ? 'ok' : 'warn'}
            />
            <ConfigSummaryTile
              label="AI-proposed"
              value={domains.filter((d) => d.origin === 'ai_proposed').length}
              sub="confirmed at setup"
            />
          </>
        )
      }
    >
      {query.isLoading ? (
        <div className="rounded-card border border-ink-100 bg-paper-0 p-card-pad text-body-sm text-ink-500">
          Loading domains…
        </div>
      ) : query.error ? (
        <ErrorMessage error={query.error} />
      ) : (
        <DomainBody
          domains={domains}
          drafts={drafts}
          onUpdate={updateDraft}
          showAddRow={showAddRow}
          setShowAddRow={setShowAddRow}
          addDraft={addDraft}
          setAddDraft={setAddDraft}
          adminProposalLoading={adminProposals.isLoading}
          adminProposalError={adminProposals.error}
          adminProposalItems={adminProposals.data?.items ?? []}
        />
      )}
    </ConfigEditorModal>
  );
}

function DomainBody({
  domains,
  drafts,
  onUpdate,
  showAddRow,
  setShowAddRow,
  addDraft,
  setAddDraft,
  adminProposalLoading,
  adminProposalError,
  adminProposalItems,
}: {
  domains: HealthDomain[];
  drafts: Record<string, DomainDraft>;
  onUpdate: (domainId: string, updates: Partial<DomainDraft>) => void;
  showAddRow: boolean;
  setShowAddRow: React.Dispatch<React.SetStateAction<boolean>>;
  addDraft: { name: string; description: string };
  setAddDraft: React.Dispatch<React.SetStateAction<{ name: string; description: string }>>;
  adminProposalLoading: boolean;
  adminProposalError: unknown;
  adminProposalItems: ReadonlyArray<{
    id: string;
    confidence: number;
    proposal_type: string;
    created_at: string;
  }>;
}) {
  const sorted = useMemo(
    () => [...domains].sort((a, b) => a.display_order - b.display_order),
    [domains],
  );
  return (
    <>
      <ConfigSection
        heading="Active domains"
        meta="Drag to reorder · order drives synthesis-report rollup"
        actions={
          !showAddRow ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAddRow(true)}>
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add custom domain
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddRow(false)}>
              Cancel
            </Button>
          )
        }
      >
        {sorted.length === 0 ? (
          <div className="px-4 py-4 text-body-sm text-ink-500">
            No active domains yet. Confirm a firm-default or AI-proposed set to seed this list.
          </div>
        ) : (
          sorted.map((d) => (
            <DomainRow key={d.id} domain={d} draft={drafts[d.id]} onUpdate={onUpdate} />
          ))
        )}
        {showAddRow ? (
          <div className="grid gap-3 border-t border-ink-100 bg-paper-50 px-4 py-3 sm:grid-cols-[1fr,2fr]">
            <div>
              <label className="block text-meta text-ink-500">Name</label>
              <input
                type="text"
                value={addDraft.name}
                onChange={(e) => setAddDraft((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Vendor methodology fit"
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
            <div>
              <label className="block text-meta text-ink-500">Description (optional)</label>
              <input
                type="text"
                value={addDraft.description}
                onChange={(e) => setAddDraft((p) => ({ ...p, description: e.target.value }))}
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
          </div>
        ) : null}
      </ConfigSection>

      <ConfigSection
        tone="ai"
        heading={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            AI proposals from vendor docs
          </span>
        }
        meta={
          adminProposalLoading
            ? 'Loading…'
            : adminProposalError
              ? 'Firm admins only.'
              : `${adminProposalItems.length} pending`
        }
      >
        {adminProposalLoading ? (
          <div className="px-4 py-4 text-meta text-ai-ink">Loading proposal queue…</div>
        ) : adminProposalError ? (
          <div className="px-4 py-4 text-meta text-ai-ink">
            Only firm admins can accept or reject these proposals. Ask a firm admin to review them.
          </div>
        ) : adminProposalItems.length === 0 ? (
          <div className="px-4 py-4 text-meta text-ai-ink">
            None pending — the next base-formation pass will populate this card.
          </div>
        ) : (
          <ul className="divide-y divide-ai-surface-border">
            {adminProposalItems.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-baseline justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-body-sm font-medium text-ai-ink">
                    {titleCase(p.proposal_type)}
                  </div>
                  <div className="mt-0.5 text-meta text-ai-ink/70">
                    Proposed {formatDate(p.created_at)}
                  </div>
                </div>
                <AiConfidenceChip value={p.confidence} />
              </li>
            ))}
          </ul>
        )}
      </ConfigSection>
    </>
  );
}

function DomainRow({
  domain,
  draft,
  onUpdate,
}: {
  domain: HealthDomain;
  draft: DomainDraft | undefined;
  onUpdate: (domainId: string, updates: Partial<DomainDraft>) => void;
}) {
  const isDirty = draft !== undefined;
  const effective: DomainDraft = draft ?? {
    name: domain.name,
    display_order: domain.display_order,
    description: domain.description,
    is_active: domain.is_active,
    rationale: '',
  };
  return (
    <div className="grid gap-3 px-4 py-3 sm:grid-cols-[16px,1fr,140px,auto]">
      <div className="pt-2 text-ink-300" aria-hidden="true">
        ≡
      </div>
      <div className="min-w-0">
        <label className="block text-meta text-ink-500">Name</label>
        <input
          type="text"
          value={effective.name}
          onChange={(e) => onUpdate(domain.id, { name: e.target.value })}
          className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        />
        <label className="mt-2 block text-meta text-ink-500">Description</label>
        <textarea
          value={effective.description ?? ''}
          onChange={(e) =>
            onUpdate(domain.id, { description: e.target.value.length > 0 ? e.target.value : null })
          }
          rows={2}
          className="mt-1 block w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        />
        {isDirty ? (
          <input
            type="text"
            placeholder={`Rationale (required for save, at least ${MIN_RATIONALE_CHARS} characters)`}
            value={effective.rationale}
            onChange={(e) => onUpdate(domain.id, { rationale: e.target.value })}
            className="mt-2 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
          />
        ) : null}
        <div className="mt-1 text-meta text-ink-500">
          Origin: <span className="font-mono">{domain.origin}</span>
        </div>
      </div>
      <div>
        <label className="block text-meta text-ink-500">Order</label>
        <input
          type="number"
          value={effective.display_order}
          onChange={(e) => onUpdate(domain.id, { display_order: Number(e.target.value) })}
          className="mt-1 h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 font-mono text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        />
      </div>
      <div>
        <button
          type="button"
          onClick={() => onUpdate(domain.id, { is_active: false })}
          disabled={!effective.is_active}
          title={
            effective.is_active
              ? 'Soft-remove from this engagement (preserves history)'
              : 'Soft-removed; will be saved on apply'
          }
          className="inline-flex h-input-h items-center gap-1 rounded-card border border-ink-200 px-2 text-meta text-posture-critical hover:bg-posture-critical-tint focus:outline-none focus:ring-2 focus:ring-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X className="h-3 w-3" aria-hidden="true" />
          {effective.is_active ? 'Soft-remove' : 'Will remove'}
        </button>
      </div>
    </div>
  );
}
